import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Skeleton — shimmer-based loading placeholder (NOT a spinner).
   Named "Spinner" per project spec but renders skeleton pulse.
   -------------------------------------------------------------------------- */

export interface SkeletonProps {
  className?: string;
  /** Render as a circle (useful for avatars). */
  circle?: boolean;
  /** Override width. */
  width?: string | number;
  /** Override height. */
  height?: string | number;
}

export function Skeleton({
  className,
  circle = false,
  width,
  height,
}: SkeletonProps) {
  return (
    <div
      className={cn(
        'skeleton',
        circle ? 'rounded-full' : 'rounded-[var(--radius-md)]',
        className,
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
      aria-hidden
    />
  );
}

/* --------------------------------------------------------------------------
   SkeletonText — multiple lines of skeleton text for paragraph loading
   -------------------------------------------------------------------------- */

export interface SkeletonTextProps {
  /** Number of text lines. */
  lines?: number;
  className?: string;
}

export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('flex flex-col gap-2.5', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={14}
          className={cn(
            'w-full',
            /* Make last line shorter for realism */
            i === lines - 1 && 'w-3/5',
          )}
        />
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
   SkeletonCard — card-shaped placeholder
   -------------------------------------------------------------------------- */

export interface SkeletonCardProps {
  className?: string;
}

export function SkeletonCard({ className }: SkeletonCardProps) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-border-default bg-surface-card p-6',
        className,
      )}
      aria-hidden
    >
      <div className="flex items-center gap-3 mb-4">
        <Skeleton circle width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton height={14} className="w-2/5" />
          <Skeleton height={12} className="w-1/3" />
        </div>
      </div>
      <SkeletonText lines={2} />
    </div>
  );
}
